import { useCallback, useEffect, useState } from 'react'
import { IQDialog, Preloader } from '../../../common'
import { colors } from '@mui/material'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { dateFormat, Format } from '../../../../utility/date-format'
import { API } from '../../../../api'
import { TADCoveredEntity, TADMembershipGroup } from '../../../../type'
import { EntitiesTypes } from '../../../../view/admin/ad-management/EntitiesTabs'

interface IProps {
  isShow: boolean
  type: string
  groupName: string
  onCancel: () => void
}

export const EntityGroupsView = ({ isShow, groupName, type, onCancel }: IProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [groupDetail, setGroupDetail] = useState<TADCoveredEntity>({} as TADCoveredEntity)
  const getGroupDetail = useCallback(async () => {
    if (isShow) {
      setIsLoading(true)
      try {
        const detail = await API.ADManagement.covered_entity.detail(groupName)

        setGroupDetail(detail)
      } finally {
        setIsLoading(false)
      }
    }
  }, [groupName, isShow])

  useEffect(() => {
    if (isShow) {
      void getGroupDetail()
    }
  }, [getGroupDetail, isShow])

  const renderPreloader = () => (
    <Preloader
      size='sm'
      style={{ display: 'block', margin: '0 auto' }}
    />
  )

  const renderGroups = (groups: TADMembershipGroup[]): string => groups?.map(({ MemberName }) => MemberName).join(', ')

  const renderContent = () => {
    const renderAddedAt = () =>
      groupDetail.WhenCreated
        ? `${dateFormat(groupDetail.WhenCreated, Format['YYYY-MM-DD'])} ${dateFormat(groupDetail.WhenCreated, Format['HH:MM:SS'])}`
        : ''

    const renderCell = (title: string, value: string, testId: string) => (
      <Grid
        item
        xs={6}
        sx={{ mb: 4 }}
      >
        <Typography color={colors.grey['500']}>{title}</Typography>
        <Typography
          color={colors.common.black}
          data-testid={testId}
          style={{
            wordBreak: 'break-word',
          }}
        >
          {value}
        </Typography>
      </Grid>
    )
    const renderMainFields = () => {
      if (type === EntitiesTypes.referral_types || type === EntitiesTypes.omit_measures || type === EntitiesTypes.reports) {
        return (
          <>
            {renderCell('Name', groupDetail.GroupName, 'entities-view-name')}
            {renderCell('Description', groupDetail.Description, 'entities-view-description')}
          </>
        )
      }
      return (
        <>
          {renderCell('ID', groupDetail.GroupName, 'entities-view-value')}
          {renderCell('Name', groupDetail.Description, 'entities-view-description')}
        </>
      )
    }
    return (
      <Grid
        container
        spacing={2}
      >
        {renderMainFields()}
        {type === EntitiesTypes.pharmacies &&
          renderCell('Pharmacy Groups', renderGroups(groupDetail.Members?.Pharmacies), `entities-view-${type}-groups`)}
        {type === EntitiesTypes.distributors &&
          renderCell('Distributor Groups', renderGroups(groupDetail.Members?.Distributors), `entities-view-${type}-groups`)}

        {type === EntitiesTypes.referral_types &&
          renderCell('Referral Type Groups', renderGroups(groupDetail.Members?.ReferralTypes), `entities-view-${type}-groups`)}
        {type === EntitiesTypes.omit_measures &&
          renderCell('Omit Groups', renderGroups(groupDetail.Members?.OmitGroups), `entities-view-${type}-groups`)}
        {type === EntitiesTypes.reports &&
          renderCell('Qlik Report Groups', renderGroups(groupDetail.Members?.Reports), `entities-view-${type}-groups`)}
        {/* @maksym add here when other tabs are added */}
        {renderCell('Added At', renderAddedAt(), 'entities-view-added')}
      </Grid>
    )
  }

  const renderActions = () => (
    <Button
      variant='contained'
      disabled={isLoading}
      onClick={onCancel}
      data-testid={`${type}-view-ok`}
    >
      OK
    </Button>
  )

  return (
    <IQDialog
      isShow={isShow}
      onClose={onCancel}
      title='Additional Information'
      content={isLoading ? renderPreloader() : renderContent()}
      actions={renderActions()}
      testPrefix={`${type}-view`}
    />
  )
}
